/**
 * ARC Realtime Provider
 * 
 * Connects to gateway WebSocket and handles:
 * - Real-time event updates to the store
 * - Sound notifications for alarms (controlled by settings-store)
 */

import { useEffect, useRef, useCallback } from 'react';
import { useGatewayWebSocket } from '@/hooks/use-gateway-websocket';
import { useVisibilityRefresh } from '@/hooks/use-visibility-refresh';
import { useArcStore } from '@/stores/arc-store';
import { useEmergencyStore } from '@/stores/emergency-store';
import { useSettingsStore } from '@/stores/settings-store';
import { playBuiltinSound, playCustomAudio } from '@/utils/sound-player';
import { subscribeToArcEvents } from '@/services/arc';

// Types for event handling (inline to avoid import issues)
interface ArcEventLike {
  severity?: string;
  received_at?: string;
  occurred_at?: string;
}

interface ArcRealtimeProviderProps {
  children: React.ReactNode;
  /** @deprecated — sound is now controlled by the settings store */
  soundEnabled?: boolean;
}

export function ArcRealtimeProvider({ children }: ArcRealtimeProviderProps) {
  // Sound settings from persistent store — use a ref so callbacks don't go stale
  const sounds = useSettingsStore((s) => s.sounds);
  const soundsRef = useRef(sounds);
  useEffect(() => { soundsRef.current = sounds; }, [sounds]);

  // Store actions
  const addEventsFromWebSocket = useArcStore((s) => s.addEventsFromWebSocket);
  const incrementWsEventCount = useArcStore((s) => s.incrementWsEventCount);
  const setWsState = useArcStore((s) => s.setWsState);
  const reconcilePendingEvents = useArcStore((s) => s.reconcilePendingEvents);
  const fetchActiveAlerts = useEmergencyStore((s) => s.fetchActiveAlerts);

  // ── Initial load: fetch all pending events on mount ────────
  // This ensures that when the app is opened (or refreshed), any events that
  // arrived while the browser was closed are immediately available in the store.
  useEffect(() => {
    reconcilePendingEvents();
    // Prime activeAlerts AND open the tickets Realtime subscription so the PiP
    // reflects ticket changes even when /emergency is not open.
    fetchActiveAlerts(true).then(() => {
      if (!useEmergencyStore.getState().unsubscribe) {
        useEmergencyStore.getState().subscribeToRealtime();
      }
    }).catch(() => null);
  }, []);

  // ── Supabase Realtime subscription (app-level, always active) ──
  // Acts as a backup channel for when the Gateway WebSocket is unavailable.
  // The store deduplicates by ID so events arriving via both WS and Supabase RT
  // will not cause duplicates in the event list.
  useEffect(() => {
    const unsub = subscribeToArcEvents(
      (event) => { addEventsFromWebSocket([event]); },
      (update) => {
        // Patch acknowledged/resolved state when event is updated by any view
        useArcStore.setState((state) => ({
          events: state.events.map(e =>
            e.id === update.id
              ? { ...e, acknowledged_at: update.acknowledged_at ?? e.acknowledged_at, resolved_at: update.resolved_at ?? e.resolved_at }
              : e
          ),
        }));
      },
      'arc-events-provider',
    );
    return unsub;
  }, [addEventsFromWebSocket]);

  // Track previous connection state to detect reconnects
  const prevConnectionState = useRef<string>('disconnected');
  const disconnectedAt = useRef<number>(0);
  const lastReconcileAt = useRef<number>(0);

  /** Play a configured sound slot */
  const playSlot = useCallback((slotKey: 'critical' | 'alarm' | 'warning' | 'processConfirm') => {
    const s = soundsRef.current;
    if (!s.masterEnabled) return;
    const slot = s[slotKey];
    if (!slot.enabled) return;

    if (slot.custom?.dataUrl) {
      playCustomAudio(slot.custom.dataUrl, s.volume);
    } else {
      playBuiltinSound(slot.builtin, s.volume);
    }
  }, []);

  // Handle incoming events
  const handleArcEvents = useCallback((rawEvents: unknown[]) => {
    addEventsFromWebSocket(rawEvents as never[]);
    incrementWsEventCount(rawEvents.length);

    // Delayed events (panel offline, replaying its buffer) are history: no alarm sound.
    const DELAYED_MS = 10 * 60_000;
    const events = (rawEvents as ArcEventLike[]).filter((e) => {
      if (!e.received_at || !e.occurred_at) return true;
      return new Date(e.received_at).getTime() - new Date(e.occurred_at).getTime() <= DELAYED_MS;
    });
    // Play the highest-priority sound present in the batch
    if (events.some((e) => e.severity === 'CRITICAL')) {
      playSlot('critical');
    } else if (events.some((e) => e.severity === 'ALARM')) {
      playSlot('alarm');
    } else if (events.some((e) => e.severity === 'WARNING')) {
      playSlot('warning');
    }
  }, [addEventsFromWebSocket, incrementWsEventCount, playSlot]);

  // Handle single event
  const handleArcEvent = useCallback((event: unknown) => {
    handleArcEvents([event]);
  }, [handleArcEvents]);

  // WebSocket connection
  const { connectionState } = useGatewayWebSocket({
    topics: ['arc'],
    onArcEvent: handleArcEvent,
    onArcEvents: handleArcEvents,
    onConnect: () => {
      setWsState('connected');
      // Only reconcile on REconnect if WS was down for >10s (not a brief blip
      // from tab switching) and at least 60s since the last reconcile.
      const downMs = Date.now() - disconnectedAt.current;
      const sinceLast = Date.now() - lastReconcileAt.current;
      if (prevConnectionState.current === 'connected' && downMs > 10_000 && sinceLast > 60_000) {
        lastReconcileAt.current = Date.now();
        reconcilePendingEvents();
      }
      prevConnectionState.current = 'connected';
    },
    onDisconnect: () => {
      setWsState('disconnected');
      disconnectedAt.current = Date.now();
      prevConnectionState.current = 'disconnected';
    },
    enabled: true,
    reconnectDelay: 5000, // 5 seconds to avoid spam
  });

  // Update store connection state when it changes
  useEffect(() => {
    if (connectionState === 'error') {
      setWsState('error');
    }
  }, [connectionState, setWsState]);

  // ── Visibility-based recovery ──────────────────────────────
  // When the user returns to the tab after >10s hidden, reconcile events
  // that may have been missed while the gateway WS and Supabase Realtime
  // were stale (browsers throttle background sockets/timers).
  // This replaces the per-page visibilitychange handlers with a single
  // centralized recovery point.
  useVisibilityRefresh({
    onVisible: () => {
      reconcilePendingEvents();
    },
    minHiddenMs: 10_000,
  });

  return <>{children}</>;
}

/**
 * Hook to get WebSocket connection status
 */
export function useArcConnectionStatus() {
  const wsConnected = useArcStore((s) => s.wsConnected);
  const wsState = useArcStore((s) => s.wsState);
  const wsEventCount = useArcStore((s) => s.wsEventCount);
  const pendingCount = useArcStore((s) => s.pendingCount);

  return { wsConnected, wsState, wsEventCount, pendingCount };
}

/**
 * Hook that exposes a function to play the process-confirmation sound.
 * Use inside any component after the operator successfully processes/closes an event.
 */
export function useProcessConfirmSound() {
  const sounds = useSettingsStore((s) => s.sounds);
  const soundsRef = useRef(sounds);
  useEffect(() => { soundsRef.current = sounds; }, [sounds]);

  return useCallback(() => {
    const s = soundsRef.current;
    if (!s.masterEnabled || !s.processConfirm.enabled) return;
    if (s.processConfirm.custom?.dataUrl) {
      playCustomAudio(s.processConfirm.custom.dataUrl, s.volume);
    } else {
      playBuiltinSound(s.processConfirm.builtin, s.volume);
    }
  }, []);
}
